import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ModalsWrapperContext, useToast } from '@kanda-libs/ks-design-library';
import {
  type Job,
  type Service,
  actions,
  services,
  useSubmit,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import useReloadInitialData from 'hooks/useReloadInitialData';
import { selectors } from 'store';
import jobSelectors from 'store/selectors/job';
import { serviceConvert } from 'utils';

import { MARK_COMPLETE_MODAL_ID } from './constants';

export interface MarkJobAsCompleteButtonPropsHook {
  showContent: boolean;
  onClick: () => void;
  callback: () => void;
  isSubmitting: boolean;
  isLoading: boolean;
}

export default function useMarkJobAsCompleteButtonProps(): MarkJobAsCompleteButtonPropsHook {
  const dispatch = useAppDispatch();
  const isLoading = useSelector(selectors.getIsLoading);
  const job = useSelector(jobSelectors.getEntity);
  const isSubmitting = useSelector(selectors.job.getIsSubmitting);
  const { showModal, hideModal } = useContext(ModalsWrapperContext);

  const refreshAll = useReloadInitialData();

  const { showError, showSuccess } = useToast();

  const showContent = useMemo(() => {
    if (job?.status !== 'accepted') return false;
    return true;
  }, [job]);

  const onClick = useCallback(() => {
    showModal(MARK_COMPLETE_MODAL_ID);
  }, [showModal]);

  const callback = useCallback(() => {
    const id = job?.id;
    if (!id) return;
    dispatch(
      actions.completeJob({
        params: {
          id,
        },
        onError: () => {
          showError('Error marking job complete');
          hideModal(MARK_COMPLETE_MODAL_ID);
        },
        onSuccess: () => {
          showSuccess('Job marked complete');
          hideModal(MARK_COMPLETE_MODAL_ID);
        },
      }),
    );
  }, [job]);

  return {
    showContent,
    onClick,
    callback,
    isSubmitting,
    isLoading,
  };
}
